<template>
  <div class="docs-container">
    <!-- Mobile Header -->
    <div class="mobile-header">
      <button @click="toggleSidebar" class="menu-toggle">
        <i class="pi pi-bars"></i>
      </button>
      <h1>Documentation</h1>
    </div>

    <!-- Sidebar Navigation -->
    <aside class="docs-sidebar" :class="{ 'sidebar-open': sidebarOpen }">
      <div class="sidebar-header">
        <h2>Documentation</h2>
        <button @click="toggleSidebar" class="close-sidebar">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <!-- Search -->
      <div class="search-container">
        <div class="search-input-wrapper">
          <i class="pi pi-search"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search documentation..."
            @input="handleSearch"
            class="search-input"
          />
          <button v-if="searchQuery" @click="clearSearch" class="clear-search">
            <i class="pi pi-times"></i>
          </button>
        </div>
      </div>

      <!-- Navigation Tree -->
      <nav class="docs-nav">
        <div v-if="searchQuery && searchResults.length > 0" class="search-results">
          <h3>Search Results ({{ searchResults.length }})</h3>
          <div
            v-for="result in searchResults"
            :key="result.path"
            @click="loadDocument(result.path)"
            class="nav-item search-result"
            :class="{ active: currentPath === result.path }"
          >
            <i class="pi pi-file"></i>
            <span>{{ result.name }}</span>
            <span class="result-category">{{ result.category }}</span>
          </div>
        </div>

        <div v-else-if="searchQuery && searchResults.length === 0" class="no-results">
          <i class="pi pi-search"></i>
          <p>No results found for "{{ searchQuery }}"</p>
        </div>

        <div v-else class="nav-tree">
          <!-- Home Link -->
          <div
            @click="loadDocument('MASTER_DOCUMENTATION_INDEX.md')"
            class="nav-item home-link"
            :class="{ active: currentPath === 'MASTER_DOCUMENTATION_INDEX.md' }"
          >
            <i class="pi pi-home"></i>
            <span>Documentation Home</span>
          </div>

          <!-- Categories -->
          <div v-for="category in categories" :key="category.name" class="nav-category">
            <div
              @click="toggleCategory(category.name)"
              class="category-header"
              :class="{ expanded: expandedCategories.includes(category.name) }"
            >
              <i class="pi" :class="expandedCategories.includes(category.name) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
              <i class="pi" :class="category.icon"></i>
              <span>{{ category.label }}</span>
              <span class="count">{{ category.files.length }}</span>
            </div>

            <div v-if="expandedCategories.includes(category.name)" class="category-items">
              <div
                v-for="file in category.files"
                :key="file.path"
                @click="loadDocument(file.path)"
                class="nav-item"
                :class="{ active: currentPath === file.path }"
              >
                <i class="pi pi-file"></i>
                <span>{{ file.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </aside>

    <!-- Main Content Area -->
    <main class="docs-content">
      <div v-if="loading" class="loading-state">
        <i class="pi pi-spin pi-spinner"></i>
        <p>Loading documentation...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <i class="pi pi-exclamation-triangle"></i>
        <h2>Error Loading Documentation</h2>
        <p>{{ error }}</p>
        <button @click="loadDocument(currentPath)" class="retry-button">
          <i class="pi pi-refresh"></i>
          Retry
        </button>
      </div>

      <div v-else class="content-wrapper">
        <!-- Breadcrumbs -->
        <nav class="breadcrumbs" v-if="breadcrumbs.length > 0">
          <span
            v-for="(crumb, index) in breadcrumbs"
            :key="index"
            class="breadcrumb-item"
          >
            <a v-if="index < breadcrumbs.length - 1" @click="navigateToBreadcrumb(crumb)">
              {{ crumb.label }}
            </a>
            <span v-else>{{ crumb.label }}</span>
            <i v-if="index < breadcrumbs.length - 1" class="pi pi-chevron-right"></i>
          </span>
        </nav>

        <!-- Document Header -->
        <div class="document-header" v-if="documentTitle">
          <h1>{{ documentTitle }}</h1>
          <div class="document-meta">
            <span v-if="currentCategory">
              <i class="pi pi-folder"></i>
              {{ currentCategory }}
            </span>
            <span v-if="lastModified">
              <i class="pi pi-calendar"></i>
              {{ lastModified }}
            </span>
          </div>
        </div>

        <!-- Table of Contents -->
        <div v-if="tableOfContents.length > 0" class="toc-container">
          <button @click="toggleTOC" class="toc-toggle">
            <i class="pi" :class="tocOpen ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
            Table of Contents
          </button>
          <nav v-if="tocOpen" class="table-of-contents">
            <a
              v-for="heading in tableOfContents"
              :key="heading.id"
              :href="`#${heading.id}`"
              :class="`toc-level-${heading.level}`"
              @click.prevent="scrollToHeading(heading.id)"
            >
              {{ heading.text }}
            </a>
          </nav>
        </div>

        <!-- Rendered Markdown Content -->
        <article class="markdown-content" v-html="renderedContent"></article>

        <!-- Footer Navigation -->
        <div class="doc-footer" v-if="previousDoc || nextDoc">
          <button
            v-if="previousDoc"
            @click="loadDocument(previousDoc.path)"
            class="nav-button prev-button"
          >
            <i class="pi pi-chevron-left"></i>
            <div>
              <span class="nav-label">Previous</span>
              <span class="nav-title">{{ previousDoc.name }}</span>
            </div>
          </button>
          <button
            v-if="nextDoc"
            @click="loadDocument(nextDoc.path)"
            class="nav-button next-button"
          >
            <div>
              <span class="nav-label">Next</span>
              <span class="nav-title">{{ nextDoc.name }}</span>
            </div>
            <i class="pi pi-chevron-right"></i>
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import MarkdownIt from 'markdown-it'
import hljs from 'highlight.js'
import 'highlight.js/styles/github-dark.css'

export default {
  name: 'DocsView',
  setup() {
    const route = useRoute()
    const router = useRouter()

    // State
    const loading = ref(true)
    const error = ref(null)
    const sidebarOpen = ref(false)
    const searchQuery = ref('')
    const searchResults = ref([])
    const expandedCategories = ref(['api', 'architecture', 'security'])
    const tocOpen = ref(true)
    const currentPath = ref('MASTER_DOCUMENTATION_INDEX.md')
    const documentContent = ref('')
    const documentTitle = ref('')
    const currentCategory = ref('')
    const lastModified = ref('')
    const categories = ref([])
    const allFiles = ref([])
    const tableOfContents = ref([])

    // Markdown renderer with syntax highlighting
    const md = new MarkdownIt({
      html: false,   // never render raw author HTML (the Docs view is retired; see server.js)
      linkify: true,
      typographer: true,
      highlight: function (str, lang) {
        if (lang && hljs.getLanguage(lang)) {
          try {
            return hljs.highlight(str, { language: lang }).value
          } catch (__) {}
        }
        return ''
      }
    })

    // Computed
    const renderedContent = computed(() => {
      if (!documentContent.value) return ''
      return md.render(documentContent.value)
    })

    const breadcrumbs = computed(() => {
      const crumbs = [{ label: 'Documentation', path: 'MASTER_DOCUMENTATION_INDEX.md' }]
      if (currentCategory.value) {
        crumbs.push({ label: currentCategory.value, path: currentCategory.value })
      }
      if (documentTitle.value && documentTitle.value !== 'Documentation') {
        crumbs.push({ label: documentTitle.value, path: currentPath.value })
      }
      return crumbs
    })

    const previousDoc = computed(() => {
      // Find previous document in current category
      return null // TODO: Implement based on category order
    })

    const nextDoc = computed(() => {
      // Find next document in current category
      return null // TODO: Implement based on category order
    })

    // Methods
    const loadDocumentList = async () => {
      try {
        const response = await axios.get('/api/docs/list')
        categories.value = response.data.categories
        allFiles.value = response.data.files
      } catch (err) {
        console.error('Failed to load document list:', err)
        error.value = 'Failed to load documentation list'
      }
    }

    const loadDocument = async (path) => {
      loading.value = true
      error.value = null
      currentPath.value = path

      try {
        const response = await axios.get(`/api/docs/content?path=${encodeURIComponent(path)}`)
        documentContent.value = response.data.content
        documentTitle.value = response.data.title || extractTitle(response.data.content)
        currentCategory.value = response.data.category || ''
        lastModified.value = response.data.lastModified || ''

        // Extract table of contents
        extractTableOfContents(response.data.content)

        // Update URL without page reload
        router.push({ query: { doc: path } })

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Close sidebar on mobile
        if (window.innerWidth <= 768) {
          sidebarOpen.value = false
        }
      } catch (err) {
        console.error('Failed to load document:', err)
        error.value = `Failed to load document: ${err.message}`
      } finally {
        loading.value = false
      }
    }

    const extractTitle = (content) => {
      const match = content.match(/^#\s+(.+)$/m)
      return match ? match[1] : 'Documentation'
    }

    const extractTableOfContents = (content) => {
      const headings = []
      const lines = content.split('\n')

      lines.forEach((line, index) => {
        const match = line.match(/^(#{2,4})\s+(.+)$/)
        if (match) {
          const level = match[1].length
          const text = match[2]
          const id = text.toLowerCase().replace(/[^\w]+/g, '-')
          headings.push({ level, text, id })
        }
      })

      tableOfContents.value = headings
    }

    const toggleCategory = (categoryName) => {
      const index = expandedCategories.value.indexOf(categoryName)
      if (index > -1) {
        expandedCategories.value.splice(index, 1)
      } else {
        expandedCategories.value.push(categoryName)
      }
    }

    const toggleSidebar = () => {
      sidebarOpen.value = !sidebarOpen.value
    }

    const toggleTOC = () => {
      tocOpen.value = !tocOpen.value
    }

    const scrollToHeading = (id) => {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    const handleSearch = () => {
      if (!searchQuery.value.trim()) {
        searchResults.value = []
        return
      }

      const query = searchQuery.value.toLowerCase()
      const results = allFiles.value.filter(file => {
        return (
          file.name.toLowerCase().includes(query) ||
          file.path.toLowerCase().includes(query) ||
          (file.category && file.category.toLowerCase().includes(query))
        )
      })

      searchResults.value = results
    }

    const clearSearch = () => {
      searchQuery.value = ''
      searchResults.value = []
    }

    const navigateToBreadcrumb = (crumb) => {
      if (crumb.path) {
        loadDocument(crumb.path)
      }
    }

    // Lifecycle
    onMounted(async () => {
      await loadDocumentList()

      // Load document from URL query or default
      const docPath = route.query.doc || 'MASTER_DOCUMENTATION_INDEX.md'
      await loadDocument(docPath)
    })

    // Watch for URL changes
    watch(
      () => route.query.doc,
      (newDoc) => {
        if (newDoc && newDoc !== currentPath.value) {
          loadDocument(newDoc)
        }
      }
    )

    return {
      loading,
      error,
      sidebarOpen,
      searchQuery,
      searchResults,
      expandedCategories,
      tocOpen,
      currentPath,
      renderedContent,
      documentTitle,
      currentCategory,
      lastModified,
      categories,
      breadcrumbs,
      tableOfContents,
      previousDoc,
      nextDoc,
      toggleCategory,
      toggleSidebar,
      toggleTOC,
      scrollToHeading,
      loadDocument,
      handleSearch,
      clearSearch,
      navigateToBreadcrumb
    }
  }
}
</script>

<style scoped>
.docs-container {
  display: flex;
  min-height: 100vh;
  background: #f9fafb;
  padding-top: 70px; /* Account for fixed navbar */
}

/* Mobile Header */
.mobile-header {
  display: none;
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background: white;
  padding: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 999;
  align-items: center;
  gap: 1rem;
}

.mobile-header h1 {
  margin: 0;
  font-size: 1.25rem;
  color: white;
}

.mobile-header {
  background: var(--ds-primary-900, #0a2540);
  background-image: url('/media/recraft/textures/texture-grid.svg');
}

.menu-toggle {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
}

/* Sidebar */
.docs-sidebar {
  width: 320px;
  background: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  position: fixed;
  top: 70px;
  left: 0;
  bottom: 0;
  z-index: 1000;
  transition: transform 0.3s ease;
}

.sidebar-header {
  padding: 1.5rem;
  border-bottom: 2px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--ds-primary-900, #0a2540);
  background-image: url('/media/recraft/textures/texture-grid.svg');
  position: relative;
}

.sidebar-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 37, 64, 0.92);
  z-index: 0;
}

.sidebar-header h2 {
  margin: 0;
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  position: relative;
  z-index: 1;
}

.close-sidebar {
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  position: relative;
  z-index: 1;
}

/* Search */
.search-container {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-wrapper .pi-search {
  position: absolute;
  left: 1rem;
  color: #999;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 2.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #1a7fd4;
  box-shadow: 0 0 0 3px rgba(26, 127, 212, 0.1);
}

.clear-search {
  position: absolute;
  right: 0.5rem;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0.5rem;
}

/* Navigation */
.docs-nav {
  padding: 1rem 0;
}

.nav-tree {
  padding: 0 0.5rem;
}

.home-link {
  margin: 0.5rem 0;
  padding: 0.75rem 1rem;
  background: #0a2540;
  color: white !important;
  border-radius: 8px;
  font-weight: 600;
}

.home-link i {
  color: white;
}

.nav-category {
  margin-bottom: 0.5rem;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-weight: 600;
  color: #333;
}

.category-header:hover {
  background: #f8f9fa;
}

.category-header .count {
  margin-left: auto;
  background: #e0e0e0;
  color: #666;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
}

.category-items {
  padding-left: 1.5rem;
  margin-top: 0.25rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1rem;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  color: #555;
  margin-bottom: 0.25rem;
}

.nav-item:hover {
  background: #f8f9fa;
  color: #1a7fd4;
}

.nav-item.active {
  background: rgba(26, 127, 212, 0.08);
  color: #1a7fd4;
  font-weight: 600;
  border-left: 3px solid #1a7fd4;
}

.nav-item i {
  font-size: 0.9rem;
  color: #999;
}

.nav-item.active i {
  color: #1a7fd4;
}

/* Search Results */
.search-results h3 {
  padding: 0.5rem 1.5rem;
  margin: 0;
  font-size: 0.9rem;
  color: #666;
  text-transform: uppercase;
}

.search-result {
  flex-direction: column;
  align-items: flex-start;
}

.result-category {
  font-size: 0.8rem;
  color: #999;
  margin-top: 0.25rem;
}

.no-results {
  text-align: center;
  padding: 2rem;
  color: #999;
}

.no-results i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* Main Content */
.docs-content {
  flex: 1;
  margin-left: 320px;
  padding: 2rem;
  max-width: 100%;
  background: white;
  min-height: calc(100vh - 70px);
}

.content-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

/* Loading & Error States */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #1a7fd4;
}

.loading-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #dc3545;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: #1a7fd4;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Breadcrumbs */
.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e0e0e0;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #666;
}

.breadcrumb-item a {
  color: #1a7fd4;
  cursor: pointer;
  text-decoration: none;
}

.breadcrumb-item a:hover {
  text-decoration: underline;
}

/* Document Header */
.document-header {
  margin-bottom: 2rem;
}

.document-header h1 {
  color: #0a2540;
  margin-bottom: 1rem;
  font-size: 2.5rem;
  font-weight: 700;
}

.document-meta {
  display: flex;
  gap: 1.5rem;
  font-size: 0.9rem;
  color: #666;
}

.document-meta span {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Table of Contents */
.toc-container {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 2rem;
}

.toc-toggle {
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1a7fd4;
  cursor: pointer;
  font-size: 1rem;
  width: 100%;
  text-align: left;
  padding: 0.5rem;
}

.table-of-contents {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
}

.table-of-contents a {
  color: #555;
  text-decoration: none;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.table-of-contents a:hover {
  background: white;
  color: #1a7fd4;
}

.toc-level-2 {
  padding-left: 1rem;
}

.toc-level-3 {
  padding-left: 2rem;
  font-size: 0.9rem;
}

.toc-level-4 {
  padding-left: 3rem;
  font-size: 0.85rem;
}

/* Markdown Content */
.markdown-content {
  line-height: 1.8;
  color: #333;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4) {
  color: #0a2540;
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.markdown-content :deep(h1) {
  font-size: 2.5rem;
  border-bottom: 3px solid #0a2540;
  padding-bottom: 0.5rem;
}

.markdown-content :deep(h2) {
  font-size: 2rem;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 0.5rem;
}

.markdown-content :deep(h3) {
  font-size: 1.5rem;
}

.markdown-content :deep(h4) {
  font-size: 1.25rem;
}

.markdown-content :deep(p) {
  margin-bottom: 1rem;
}

.markdown-content :deep(a) {
  color: #1a7fd4;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease;
}

.markdown-content :deep(a:hover) {
  border-bottom-color: #1a7fd4;
}

.markdown-content :deep(code) {
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  color: #1a7fd4;
}

.markdown-content :deep(pre) {
  background: #1e1e1e;
  border-radius: 8px;
  padding: 1.5rem;
  overflow-x: auto;
  margin: 1.5rem 0;
}

.markdown-content :deep(pre code) {
  background: none;
  color: #d4d4d4;
  padding: 0;
}

.markdown-content :deep(blockquote) {
  border-left: 4px solid #1a7fd4;
  padding-left: 1rem;
  margin: 1.5rem 0;
  color: #666;
  font-style: italic;
  background: #f8f9fa;
  padding: 1rem 1rem 1rem 1.5rem;
  border-radius: 4px;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 2rem;
}

.markdown-content :deep(li) {
  margin-bottom: 0.5rem;
}

.markdown-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
  padding: 1rem;
  text-align: left;
  border: 1px solid #e0e0e0;
}

.markdown-content :deep(th) {
  background: #0a2540;
  color: white;
  font-weight: 600;
}

.markdown-content :deep(tr:nth-child(even)) {
  background: #f8f9fa;
}

.markdown-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 1.5rem 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

/* Footer Navigation */
.doc-footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 2px solid #e0e0e0;
}

.nav-button {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  max-width: 45%;
}

.nav-button:hover {
  border-color: #1a7fd4;
  box-shadow: 0 4px 15px rgba(26, 127, 212, 0.1);
}

.nav-button div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.nav-label {
  font-size: 0.8rem;
  color: #999;
  text-transform: uppercase;
}

.nav-title {
  font-weight: 600;
  color: #333;
}

.next-button {
  text-align: right;
  margin-left: auto;
}

/* Responsive Design */
@media (max-width: 768px) {
  .mobile-header {
    display: flex;
  }

  .docs-container {
    padding-top: 130px;
  }

  .docs-sidebar {
    transform: translateX(-100%);
    top: 130px;
  }

  .docs-sidebar.sidebar-open {
    transform: translateX(0);
  }

  .close-sidebar {
    display: block;
  }

  .docs-content {
    margin-left: 0;
    padding: 1rem;
  }

  .document-header h1 {
    font-size: 1.75rem;
  }

  .markdown-content :deep(h1) {
    font-size: 1.75rem;
  }

  .markdown-content :deep(h2) {
    font-size: 1.5rem;
  }

  .doc-footer {
    flex-direction: column;
  }

  .nav-button {
    max-width: 100%;
  }
}
</style>
